import { useSidebarContext } from './SidebarContext.js';

/**
 * Navigation item for the sidebar, with icon + label + optional badge count.
 * Automatically switches to icon-only mode when the sidebar is collapsed.
 * On mobile, clicking an item auto-closes the drawer.
 *
 * @param {object} props
 * @param {import('lucide-react').LucideIcon} props.icon - Lucide icon component
 */
const SidebarItem = ({ icon: IconComp, label, active = false, badge, onClick, className = '' }) => {
  const { collapsed, closeMobile } = useSidebarContext();

  const handleClick = () => {
    onClick?.();
    closeMobile?.();
  };

  return (
    <button
      onClick={handleClick}
      title={collapsed ? label : undefined}
      aria-current={active ? 'page' : undefined}
      className={[
        'group flex w-full items-center rounded-lg text-sm font-medium',
        'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
        active
          ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
          : 'text-content-secondary hover:bg-surface-hover hover:text-content-hover',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {IconComp && (
        <IconComp
          size={18}
          className={
            active
              ? 'text-brand-600 dark:text-brand-400'
              : 'text-gray-400 group-hover:text-content-secondary'
          }
        />
      )}
      {!collapsed && <span className="flex-1 text-left">{label}</span>}
      {!collapsed && badge !== undefined && badge > 0 && (
        <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-900 dark:text-brand-300">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
};

export default SidebarItem;
